/*
================================================================================
|                                                                              |
|  FILE: src/components/common/ImageSlider.js                                  |
|  PURPOSE: Reusable image slider component for EcoRevive project images.     |
|  NOTE: Optimized with lazy loading and performance improvements.             |
|                                                                              |
================================================================================
*/
import React, { useState, useEffect, useCallback } from 'react';
import OptimizedImage from './OptimizedImage';

export default function ImageSlider({ images, className = "", showIndicators = true, autoPlay = true, interval = 4000 }) {
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        if (!autoPlay) return;

        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % images.length);
        }, interval);

        return () => clearInterval(timer);
    }, [images.length, interval, autoPlay]);

    const goToSlide = useCallback((index) => {
        setCurrentSlide(index);
    }, []);

    // Remove unused functions to eliminate warnings
    // const goToPrevious = () => {
    //     setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
    // };

    // const goToNext = () => {
    //     setCurrentSlide((prev) => (prev + 1) % images.length);
    // };

    return (
        <div className={`relative ${className}`}>
            {/* Slider Container with overflow hidden */}
            <div className="relative overflow-hidden rounded-2xl shadow-lg h-full w-full">
                {/* Image Container */}
                <div className="relative h-full w-full">
                    {images.map((image, index) => (
                        <div
                            key={index}
                            className={`absolute inset-0 transition-opacity duration-1000 ${
                                index === currentSlide ? 'opacity-100' : 'opacity-0'
                            }`}
                        >
                            <OptimizedImage
                                src={image.src}
                                alt={image.alt}
                                className="w-full h-80 object-cover"
                                effect="blur"
                                threshold={100}
                            />
                        </div>
                    ))}
                </div>

                {/* Indicators */}
                {showIndicators && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                        {images.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => goToSlide(index)}
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                    index === currentSlide 
                                        ? 'bg-white shadow-lg' 
                                        : 'bg-white/50 hover:bg-white/75'
                                }`}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Fixed Overlay - Outside the overflow-hidden container */}
            {images.some(img => img.overlay) && (
                <div className="absolute -bottom-4 -right-4 bg-white p-4 rounded-xl shadow-lg z-20">
                    <p className="text-sm font-semibold text-gray-900">Project EcoRevive</p>
                    <p className="text-xs text-gray-500">Environmental Recovery Initiative</p>
                </div>
            )}
        </div>
    );
} 